import {
    AdminUser,
    PLATFORM_ROLES,
    PlatformRole,
    assignPlatformRole,
    deleteAdminUser,
    isPlatformRole,
    listAdminUsers,
    removePlatformRole,
} from '../../data/AdminUser';
import { apiErrorMessage } from '../../data/apiClient';
import { useAuth } from '../../hooks/useAuth';
import { useCsrf } from '../../hooks/useCsrf';
import { usePermissions } from '../../hooks/usePermissions';
import {
    ActionIcon,
    Avatar,
    Badge,
    Button,
    Flex,
    Group,
    Loader,
    Modal,
    Paper,
    SimpleGrid,
    Skeleton,
    Stack,
    Switch,
    Text,
    Title,
    Tooltip,
    useComputedColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconShieldLock, IconTrash } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import useSWR from 'swr';

/** Shorthand shown on the toggle; the full name is the API contract. */
const shortRoleLabel = (role: PlatformRole): string =>
    role.replace(/^Watchly /, '');

/**
 * Platform roles are visually separated from tenant roles: a solid filled badge
 * with a shield, versus a muted outline. The distinction matters at a glance —
 * a platform role grants authority across every organization, a tenant role
 * only inside one.
 */
const RoleBadge = ({ role }: { role: string }) => {
    const scheme = useComputedColorScheme('light');

    if (!isPlatformRole(role)) {
        return (
            <Badge variant="outline" color="gray" styles={{ label: { fontWeight: 500 } }}>
                {role}
            </Badge>
        );
    }

    /*
     * The filled badge resolves to a light grey in dark mode (neutral shade 5),
     * and Mantine's default white label is then near-invisible on it. Light mode
     * fills with a dark grey, where white is correct — so only the dark case is
     * overridden.
     */
    const labelColor = scheme === 'dark' ? '#212529' : undefined;

    return (
        <Badge
            variant="filled"
            color="neutral"
            leftSection={<IconShieldLock size={12} stroke={2.2} color={labelColor} />}
            styles={{
                label: { fontWeight: 700, letterSpacing: 0.2, color: labelColor },
            }}
        >
            {role}
        </Badge>
    );
};

/**
 * Platform-wide user administration. Watchly super admins only.
 */
const AdminUsersPage = () => {
    const { showAdminTools, isSuperAdmin, isLoading: authLoading } = usePermissions();
    const { user: currentUser } = useAuth();
    const { csrfToken } = useCsrf();

    const {
        data: users,
        error,
        isLoading,
        mutate,
    } = useSWR<AdminUser[], AxiosError>(showAdminTools ? 'api/admin/users' : null, listAdminUsers);

    const [target, setTarget] = useState<AdminUser | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [isDeleting, setIsDeleting] = useState(false);
    // Keyed `${userId}:${role}` so only the toggle being changed shows as busy.
    const [pendingRole, setPendingRole] = useState<string | null>(null);

    const togglePlatformRole = async (
        user: AdminUser,
        role: PlatformRole,
        granted: boolean,
    ) => {
        setPendingRole(`${user.id}:${role}`);
        try {
            granted
                ? await assignPlatformRole(csrfToken, user.id, role)
                : await removePlatformRole(csrfToken, user.id, role);

            await mutate();
            notifications.show({
                message: granted
                    ? `${user.name} is now a ${shortRoleLabel(role)}.`
                    : `Removed ${shortRoleLabel(role)} from ${user.name}.`,
            });
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not update platform role',
                message: apiErrorMessage(err),
            });
        } finally {
            setPendingRole(null);
        }
    };

    const confirmDelete = (user: AdminUser) => {
        setTarget(user);
        open();
    };

    const handleDelete = async () => {
        if (!target) return;
        setIsDeleting(true);
        try {
            await deleteAdminUser(csrfToken, target.id);
            await mutate();
            notifications.show({ message: `Deleted ${target.name}.` });
            close();
            setTarget(null);
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not delete user',
                message: apiErrorMessage(err),
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (authLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    // UX gate only — /api/admin/users enforces the role server-side. Reached by
    // URL only, since the nav entry is hidden in the same conditions.
    if (!showAdminTools) {
        return (
            <Stack px="md">
                <Title order={3}>Users</Title>
                <Text c="dimmed">
                    {isSuperAdmin
                        ? 'Exit the customer view to manage platform users.'
                        : 'You don’t have permission to view platform users.'}
                </Text>
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack px="md">
                <Title order={3}>Users</Title>
                <Text c="red">Error: {error.message}</Text>
            </Stack>
        );
    }

    return (
        <Stack px="md" gap="lg">
            <div>
                <Title order={3}>Users</Title>
                <Text size="sm" c="dimmed">
                    Every user across all organizations.
                </Text>
            </div>

            {isLoading ? (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                    {[0, 1, 2].map(i => (
                        <Skeleton key={i} height={132} radius="md" />
                    ))}
                </SimpleGrid>
            ) : users && users.length > 0 ? (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                    {users.map(user => {
                        const isSelf = user.id === currentUser?.id;

                        return (
                            <Paper
                                key={user.id}
                                shadow="xs"
                                p="lg"
                                radius="md"
                                withBorder
                                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                            >
                                <Flex justify="space-between" align="flex-start" gap="xs">
                                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                                        <Avatar src={user.avatarUrl} radius="xl" size={40}>
                                            {user.name?.charAt(0)?.toUpperCase()}
                                        </Avatar>
                                        <Stack gap={2} style={{ minWidth: 0 }}>
                                            <Text fw={600} truncate>
                                                {user.name}
                                            </Text>
                                            <Text size="xs" c="dimmed" truncate>
                                                {user.email}
                                            </Text>
                                        </Stack>
                                    </Group>
                                    <Tooltip
                                        label={isSelf ? 'You can’t delete yourself' : 'Delete user'}
                                        withArrow
                                    >
                                        <div>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                disabled={isSelf}
                                                onClick={() => confirmDelete(user)}
                                                aria-label={`Delete ${user.name}`}
                                            >
                                                <IconTrash size={18} />
                                            </ActionIcon>
                                        </div>
                                    </Tooltip>
                                </Flex>

                                <Text size="sm" c="dimmed">
                                    {user.org?.name ?? 'No organization'}
                                </Text>

                                <Group gap="xs">
                                    {user.roles.length > 0 ? (
                                        user.roles.map(role => (
                                            <RoleBadge key={role} role={role} />
                                        ))
                                    ) : (
                                        <Text size="xs" c="dimmed">
                                            No roles — cannot sign in
                                        </Text>
                                    )}
                                </Group>

                                {/*
                                 * Only offered for the internal org: a platform
                                 * role applies across every organization, so the
                                 * API refuses to grant one to a customer's user.
                                 */}
                                {user.org?.isInternal && (
                                    <Stack gap={6} mt="auto" pt="xs">
                                        {PLATFORM_ROLES.map(role => {
                                            const held = user.roles.includes(role);
                                            const busy = pendingRole === `${user.id}:${role}`;

                                            return (
                                                <Tooltip
                                                    key={role}
                                                    label="You can’t change your own platform roles"
                                                    disabled={!isSelf}
                                                    withArrow
                                                >
                                                    <div>
                                                        <Switch
                                                            checked={held}
                                                            disabled={isSelf || busy}
                                                            onChange={event =>
                                                                togglePlatformRole(
                                                                    user,
                                                                    role,
                                                                    event.currentTarget.checked,
                                                                )
                                                            }
                                                            size="sm"
                                                            label={
                                                                <Text size="xs" fw={held ? 700 : 500}>
                                                                    {shortRoleLabel(role)}
                                                                </Text>
                                                            }
                                                            thumbIcon={
                                                                held ? (
                                                                    <IconShieldLock
                                                                        size={10}
                                                                        stroke={2.5}
                                                                        color="#343a40"
                                                                    />
                                                                ) : undefined
                                                            }
                                                            color="neutral"
                                                        />
                                                    </div>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Paper>
                        );
                    })}
                </SimpleGrid>
            ) : (
                <Text c="dimmed">No users found.</Text>
            )}

            <Modal opened={opened} onClose={close} title="Danger Zone" centered>
                <Stack gap="sm">
                    <Text style={{ marginBottom: 16 }}>
                        Permanently delete{' '}
                        <Text span fw={700}>
                            {target?.name}
                        </Text>
                        ? This also removes their roles and cannot be undone.
                    </Text>
                    <Button color="red" onClick={handleDelete} fullWidth disabled={isDeleting}>
                        {isDeleting ? <Loader color="gray" size={18} /> : 'Confirm Delete'}
                    </Button>
                    <Button color="gray" onClick={close} fullWidth disabled={isDeleting}>
                        Cancel
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
};

export { AdminUsersPage };
